import type { PresenteBoasVindasPersistido } from '@/shared/types/presenteBoasVindas';

import { reportError } from './reportError';

// Vive em `localStorage` (e não no Redux) porque a entrada na plataforma é uma
// navegação de página inteira: o store é remontado do zero do outro lado.
const CHAVE_LEGADA = 'onboardingSelf:presenteBoasVindas';
const VERSAO_SCHEMA = 2;

const chaveDo = (usuarioId: string) => `${CHAVE_LEGADA}:${usuarioId}`;

interface RegistroPersistido extends PresenteBoasVindasPersistido {
  versao: number;
}

const numeroPositivo = (valor: unknown): valor is number =>
  typeof valor === 'number' && Number.isFinite(valor) && valor > 0;

const removerChave = (chave: string): void => {
  try {
    window.localStorage.removeItem(chave);
  } catch {
    return;
  }
};

export const salvarPresenteBoasVindas = (presente: PresenteBoasVindasPersistido): void => {
  try {
    const registro: RegistroPersistido = { versao: VERSAO_SCHEMA, ...presente };
    window.localStorage.setItem(chaveDo(presente.usuarioId), JSON.stringify(registro));
  } catch (error) {
    // Storage indisponível (aba anônima, cota estourada) não pode derrubar a
    // entrada na plataforma — o usuário só perde o banner de boas-vindas.
    reportError(error instanceof Error ? error : new Error(String(error)), {
      source: 'presenteBoasVindas.salvar',
    });
  }
};

export const lerPresenteBoasVindas = (usuarioId: string): PresenteBoasVindasPersistido | null => {
  // A chave sem escopo foi gravada pelo wizard mockado e não pertence a ninguém.
  removerChave(CHAVE_LEGADA);

  let bruto: string | null;
  try {
    bruto = window.localStorage.getItem(chaveDo(usuarioId));
  } catch {
    return null;
  }
  if (!bruto) return null;

  let parseado: unknown;
  try {
    parseado = JSON.parse(bruto);
  } catch {
    limparPresenteBoasVindas(usuarioId);
    return null;
  }
  if (typeof parseado !== 'object' || parseado === null) {
    limparPresenteBoasVindas(usuarioId);
    return null;
  }

  const dados = parseado as Partial<RegistroPersistido>;
  if (
    dados.versao !== VERSAO_SCHEMA ||
    dados.usuarioId !== usuarioId ||
    !numeroPositivo(dados.saldoNotificacoes)
  ) {
    limparPresenteBoasVindas(usuarioId);
    return null;
  }

  return {
    usuarioId,
    saldoNotificacoes: dados.saldoNotificacoes,
    primeiroNome: typeof dados.primeiroNome === 'string' ? dados.primeiroNome : '',
  };
};

export const limparPresenteBoasVindas = (usuarioId: string): void => {
  removerChave(chaveDo(usuarioId));
};
